'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { getDevicePlatform } from '@utils/segmentProperties'
import { CONTACT_US_EVENTS } from '@configs/contactus.config'
import { parseBoldText } from '@utils/parseBoldText'
import { useUserContext } from '@context/UserContext/UserContenxt'
import styles from './callback.module.scss'
import CallbackModal, { CallbackModalSchema } from './CallbackModal'

interface CallbackSectionProps {
  heading: string
  subheading: string
  ctaPrimary: string
  modalSchema: CallbackModalSchema
  CallbackMedia: {
    data: {
      id: number
      attributes: {
        url: string
        width: number
        height: number
        alternativeText: string | null
      }
    }
  }
  isMobile?: boolean
  handleModalOpen: (flag: boolean) => void
}

const CallbackSection: React.FC<CallbackSectionProps> = ({
  heading,
  subheading,
  ctaPrimary,
  modalSchema,
  CallbackMedia,
  handleModalOpen,
  isMobile = false,
}) => {
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [phoneError, setPhoneError] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isUserPermitted, setIsUserPermitted] = useState(false)

  const {
    url: imageUrl,
    alternativeText,
    width: imageWidth = 658,
    height: imageHeight = 359,
  } = CallbackMedia?.data?.attributes || {}
  const imageAlt = alternativeText || 'ACKO Expert'

  const { userData } = useUserContext()

  useEffect(() => {
    if (userData?.phone) {
      // Clean the phone number (remove non-digits and limit to 10 digits)
      const cleanedPhone = userData.phone.replace(/[^\d]/g, '').slice(0, 10)
      setPhoneNumber(cleanedPhone)
      setIsUserPermitted(
        modalSchema?.hasOwnProperty('permittedUsers') ? !!modalSchema?.permittedUsers?.includes(cleanedPhone) : true
      )
    }
  }, [userData?.phone])

  useEffect(() => {
    if (!modalSchema?.hasOwnProperty('permittedUsers')) {
      setIsUserPermitted(true)
    }
  }, [modalSchema?.permittedUsers])

  const isPhoneNumberValid = (number: string = phoneNumber) => number && number.length === 10

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '').slice(0, 10)
    setPhoneNumber(value)
    if (phoneError) setPhoneError('')
  }

  const handlePhoneBlur = () => {
    if (isPhoneNumberValid()) {
      setPhoneError('')
    } else {
      setPhoneError('Please enter a valid 10-digit mobile number')
    }
  }

  const handleCtaClick = async () => {
    if (!isPhoneNumberValid()) {
      setPhoneError('Please enter a valid 10-digit mobile number')
      return
    }

    const event = CONTACT_US_EVENTS.REQUEST_CALLBACK_CLICKED
    await trackEvent(event.eventName, {
      ...event.eventProps,
      phone: phoneNumber,
      platform: getDevicePlatform(),
    })

    setIsModalOpen(true)
    handleModalOpen(true)
  }

  const callbackModalContent = {
    ...modalSchema,
    description: parseBoldText(
      typeof modalSchema?.description === 'string' ? modalSchema.description.replace('phone_number', phoneNumber) : ''
    ),
  }

  // flag to enable/disable the callback section based on user number
  if (!isUserPermitted) return

  return (
    <section className={styles.callbackSection}>
      <div className={styles.container}>
        {imageUrl && (
          <div className={styles.imageWrapper}>
            <Image
              src={imageUrl}
              alt={imageAlt}
              width={imageWidth}
              height={imageHeight}
              className={styles.image}
              priority={false}
            />
          </div>
        )}

        <div className={styles.contentWrapper}>
          <div className={styles.textContent}>
            <h2 className={styles.heading}>{heading}</h2>
            {subheading && <p className={styles.subheading}>{parseBoldText(subheading)}</p>}
          </div>

          <div className={styles.ctaGroup}>
            <div className={styles.phoneInputWrapper}>
              <div className={styles.phoneInputContainer}>
                <span className={styles.countryCode}>+91</span>
                <input
                  type="tel"
                  className={styles.phoneInput}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  placeholder="Enter mobile number"
                  maxLength={10}
                  inputMode="numeric"
                />
              </div>
              {phoneError && <span className={styles.phoneError}>{phoneError}</span>}
            </div>

            <button
              className={styles.ctaPrimary}
              disabled={phoneError !== ''}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={handleCtaClick}
              type="button">
              {ctaPrimary}
            </button>
          </div>
        </div>
      </div>
      {modalSchema && (
        <CallbackModal
          isOpen={isModalOpen}
          isMobile={isMobile}
          onClose={() => {
            handleModalOpen(false)
            setIsModalOpen(false)
          }}
          modalSchema={callbackModalContent}
        />
      )}
    </section>
  )
}

export default CallbackSection
